import {
  Blocks,
  Braces,
  Brush,
  Droplets,
  Film,
  Layers,
  LayoutTemplate,
  MousePointerSquareDashed,
  Shapes,
  SlidersHorizontal,
  Type,
  type LucideIcon,
} from 'lucide-react';

/**
 * The left rail's sections.
 *
 * Each one owns a slice of the old sidebar, so the side panel shows just the
 * controls for what you are doing instead of ten stacked accordions. The
 * `tools` list is what the rail activates when you switch to a section — the
 * section and the tool stay in sync, which is what makes the app feel like it
 * has modes rather than a wall of buttons.
 */

export type SectionId =
  | 'draw'
  | 'select'
  | 'shapes'
  | 'text'
  | 'elements'
  | 'color'
  | 'layers'
  | 'animate'
  | 'templates'
  | 'data'
  | 'settings';

export interface Section {
  id: SectionId;
  label: string;
  title: string;
  description: string;
  icon: LucideIcon;
  /** Switching to the section selects this tool, when it has one. */
  defaultTool?: string;
}

export const SECTIONS: readonly Section[] = [
  {
    id: 'draw',
    label: 'Draw',
    title: 'Draw',
    description: 'Paint characters onto the grid. Right-drag paints with the background colour.',
    icon: Brush,
    defaultTool: 'pencil',
  },
  {
    id: 'select',
    label: 'Select',
    title: 'Select & Move',
    description: 'Select an area, then move, fill, recolour or save it. Drag inside a selection to move it.',
    icon: MousePointerSquareDashed,
    defaultTool: 'select',
  },
  {
    id: 'shapes',
    label: 'Shapes',
    title: 'Shapes & Boxes',
    description: 'Lines, rectangles, ellipses and smart boxes that connect to what they touch.',
    icon: Shapes,
    defaultTool: 'box',
  },
  {
    id: 'text',
    label: 'Text',
    title: 'Text',
    description:
      'Type on the canvas, or generate letterforms out of Braille, blocks or box drawing — solid or cut out of a slab.',
    icon: Type,
    defaultTool: 'text',
  },
  {
    id: 'elements',
    label: 'Elements',
    title: 'Elements',
    description: 'Drag out panels, tables, progress bars and other ready-made TUI widgets.',
    icon: Blocks,
    defaultTool: 'widget',
  },
  {
    id: 'color',
    label: 'Colour',
    title: 'Colour & Gradient',
    description: 'Pick colours, check how they survive ANSI 16 and 256, and build gradients.',
    icon: Droplets,
    defaultTool: 'gradient',
  },
  {
    id: 'layers',
    label: 'Layers',
    title: 'Layers',
    description: 'Stack, hide, lock and merge. Layers composite bottom-up; the list shows them top-down.',
    icon: Layers,
  },
  {
    id: 'animate',
    label: 'Animate',
    title: 'Animation',
    description: 'Build frames from layers, play them back on the canvas, and export a player.',
    icon: Film,
  },
  {
    id: 'templates',
    label: 'Templates',
    title: 'Templates',
    description: 'Start from a ready layout, or save your own design as a reusable template.',
    icon: LayoutTemplate,
  },
  {
    id: 'data',
    label: 'Data',
    title: 'Variables & Components',
    description: 'Insert {{placeholders}} that render live values, and reuse saved pieces.',
    icon: Braces,
  },
  {
    id: 'settings',
    label: 'Settings',
    title: 'Document & View',
    description: 'Size, colour mode, background, grid, rulers, safe areas and compatibility.',
    icon: SlidersHorizontal,
  },
];

export function sectionById(id: SectionId): Section {
  return SECTIONS.find((s) => s.id === id) ?? SECTIONS[0];
}
